// Vault page data hook — protected capital ledger.
//
// Reads from reserve_wallets, reserve_transactions, and sika_goals
// to produce the vault balance, per-goal allocations, and ledger
// entries. All reads are RLS-bounded. No writes, no RPCs from the
// client, no service-role client.
//
// CRITICAL RULE: The vault is deposit-in, withdraw-out. STRIKZA
// never debits from the reserve. All top-ups and withdrawals are
// user-initiated.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

type GoalRow = Database["public"]["Tables"]["sika_goals"]["Row"];

export interface VaultWallet {
  bettingBalance: number;
  investmentBalance: number;
  totalDeposited: number;
  totalWithdrawn: number;
  totalProtected: number;
  lastToppedUp: string | null;
}

export interface VaultGoalAllocation {
  id: string;
  title: string;
  category: string | null;
  protectedAmount: number;
  targetAmount: number;
  pct: number;
  status: string;
}

export interface VaultLedgerEntry {
  id: string;
  direction: "in" | "out";
  walletType: string;
  txnType: string;
  label: string;
  detail: string;
  goalId: string | null;
  when: string;
  ref: string;
  amount: number;
}

export interface VaultLedgerState {
  /** Combined vault balance (betting + investment reserves). */
  balance: number;
  /** Wallet breakdown. */
  wallet: VaultWallet;
  /** Per-goal allocations from sika_goals.protected_amount. */
  goals: VaultGoalAllocation[];
  /** Unallocated vault balance. */
  unallocated: number;
  /** Transaction ledger from reserve_transactions. */
  entries: VaultLedgerEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const GOAL_COLORS = [
  "var(--accent)",
  "#4ec9a0",
  "#6aa9e9",
  "#e9a06a",
  "#c97ae9",
  "#e96a8d",
];

export function goalColor(index: number): string {
  const c: string | undefined = GOAL_COLORS[index % GOAL_COLORS.length];
  return c !== undefined ? c : "var(--accent)";
}

const EMPTY_WALLET: VaultWallet = {
  bettingBalance: 0,
  investmentBalance: 0,
  totalDeposited: 0,
  totalWithdrawn: 0,
  totalProtected: 0,
  lastToppedUp: null,
};

const TXN_LABELS: Record<string, string> = {
  topup: "Top-up",
  protection: "Auto-protect",
  withdrawal: "Withdrawal",
  refund: "Refund",
};

const WALLET_LABELS: Record<string, string> = {
  betting: "Betting reserve",
  investment: "Investment reserve",
  protection: "Chain protection",
};

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    const mon = d.toLocaleString("en-GB", { month: "short" });
    const day = d.getDate();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${mon} ${day} · ${hh}:${mm}`;
  } catch {
    return iso;
  }
}

export function useVaultLedger(): VaultLedgerState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [wallet, setWallet] = useState<VaultWallet>(EMPTY_WALLET);
  const [goals, setGoals] = useState<VaultGoalAllocation[]>([]);
  const [entries, setEntries] = useState<VaultLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setWallet(EMPTY_WALLET);
      setGoals([]);
      setEntries([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // 1. Fetch wallet balance
    const walletRes = await supabase
      .from("reserve_wallets")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    const w = walletRes.data;
    const walletData: VaultWallet = w
      ? {
          bettingBalance: Number(w.betting_balance) || 0,
          investmentBalance: Number(w.investment_balance) || 0,
          totalDeposited: Number(w.total_deposited) || 0,
          totalWithdrawn: Number(w.total_withdrawn) || 0,
          totalProtected: Number(w.total_protected) || 0,
          lastToppedUp: w.last_topped_up,
        }
      : EMPTY_WALLET;
    setWallet(walletData);

    // 2. Fetch transactions (ledger)
    const txnRes = await supabase
      .from("reserve_transactions")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(100);

    if (txnRes.error) {
      console.error("[vault] transactions fetch:", txnRes.error.message);
    }

    const txnRows = txnRes.data ?? [];
    const ledgerEntries: VaultLedgerEntry[] = txnRows.map((r) => {
      const txnType = r.txn_type;
      const walletType = r.wallet_type;
      const isOut = txnType === "withdrawal";
      return {
        id: r.id,
        direction: isOut ? "out" as const : "in" as const,
        walletType,
        txnType,
        label: TXN_LABELS[txnType] ?? txnType,
        detail: `${WALLET_LABELS[walletType] ?? walletType}${r.description ? ` · ${r.description}` : ""}`,
        goalId: r.goal_id,
        when: formatDate(r.created_at),
        ref: r.reference ?? `TX-${r.id.slice(0, 6).toUpperCase()}`,
        amount: Number(r.amount) || 0,
      };
    });
    setEntries(ledgerEntries);

    // 3. Goals with protected_amount
    const goalRes = await supabase
      .from("sika_goals")
      .select("id, title, goal_category, protected_amount, target_amount, status")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (goalRes.error) {
      console.error("[vault] goals fetch:", goalRes.error.message);
    }
    const goalRows = (goalRes.data ?? []) as GoalRow[];
    const totalBalance = walletData.bettingBalance + walletData.investmentBalance;

    const goalAllocations: VaultGoalAllocation[] = goalRows
      .filter((g) => g.status !== "cancelled" && g.status !== "abandoned")
      .map((g) => ({
        id: g.id,
        title: g.title,
        category: g.goal_category,
        protectedAmount: Number(g.protected_amount) || 0,
        targetAmount: Number(g.target_amount) || 0,
        pct: totalBalance > 0
          ? ((Number(g.protected_amount) || 0) / totalBalance) * 100
          : 0,
        status: g.status,
      }));
    setGoals(goalAllocations);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const balance = wallet.bettingBalance + wallet.investmentBalance;

  const unallocated = useMemo(() => {
    const allocated = goals.reduce((s, g) => s + g.protectedAmount, 0);
    return Math.max(0, balance - allocated);
  }, [balance, goals]);

  return useMemo<VaultLedgerState>(
    () => ({ balance, wallet, goals, unallocated, entries, loading, error, refetch }),
    [balance, wallet, goals, unallocated, entries, loading, error, refetch],
  );
}
